import { format, isToday, isTomorrow, isValid, parse, parseISO } from 'date-fns';
import { CADENA_VACIA } from 'utils/constants';

/**
 * Constante que contiene el formato de día, mes y año.
 */
export const FORMATO_DIA_MES_ANIO = 'dd/MM/yyyy';

/**
 * Constante que contiene el formato de hora.
 */
export const FORMATO_HORA = 'HH:mm';

/**
 * Constante que contiene el formato de última actualización en la UI.
 */
export const FORMATO_ULTIMA_ACTUALIZACION = 'dd/MM/yyyy - HH:mm';

/**
 * Constante que contiene la etiqueta por defecto de hoy.
 */
const ETIQUETA_HOY = 'Hoy';

/**
 * Constante que contiene la etiqueta por defecto de mañana.
 */
const ETIQUETA_MANANA = 'Mañana';

/**
 * Constante que contiene el conector entre el día y la hora.
 */
export const ETIQUETA_A_LAS = 'a las';

/**
 * Constante que contiene el sufijo de hora en la UI.
 */
export const SUFIJO_HORAS = ' hrs.';

/**
 * Constante que contiene el separador entre fecha y hora ya formateadas.
 */
const SEPARADOR_FECHA_HORA = ' - ';

/**
 * Constante que contiene el desplazamiento del mes, porque Date usa mes base cero.
 */
const DESPLAZAMIENTO_MES = 1;

/**
 * Constante que contiene el largo mínimo de día, mes, hora y minuto.
 */
const LARGO_DOS_DIGITOS = 2;

/**
 * Constante que contiene los segundos cuando la fecha no los trae.
 */
const SEGUNDOS_POR_DEFECTO = 0;

/**
 * Constante que contiene el carácter de relleno de la fecha.
 */
const CARACTER_RELLENO = '0';

/**
 * Constante que contiene el texto por defecto del pie cuando la actualización es de hoy.
 */
const PREFIJO_INFO_HOY = 'Información actualizada hoy a las ';

/**
 * Constante que contiene el texto por defecto del pie cuando la actualización es de otro día.
 */
const PREFIJO_INFO_OTRO_DIA = 'Información actualizada al ';

/**
 * Casuística de fecha cuando el evento es hoy.
 */
export const TIPO_FECHA_HOY = 'hoy';

/**
 * Casuística de fecha cuando el evento es mañana.
 */
export const TIPO_FECHA_MANANA = 'manana';

/**
 * Casuística de fecha cuando el evento es otro día.
 */
export const TIPO_FECHA_OTRO_DIA = 'fecha';

/**
 * Casuística de fecha cuando no hay valor.
 */
export const TIPO_FECHA_VACIO = 'vacio';

/**
 * Indica si el texto contiene solo dígitos.
 * @param valor fragmento a validar
 * @returns true si todos los caracteres son dígitos
 */
const esSoloDigitos = (valor: string): boolean => {
  if (valor.length === 0) {
    return false;
  }
  for (let i = 0; i < valor.length; i += 1) {
    const codigo = valor.charCodeAt(i);
    if (codigo < 48 || codigo > 57) {
      return false;
    }
  }
  return true;
};

/**
 * Convierte un fragmento numérico a entero si es válido.
 * @param valor texto numérico
 * @param largoMin largo mínimo permitido
 * @param largoMax largo máximo permitido
 * @returns número o null
 */
const parsearEnteroAcotado = (valor: string, largoMin: number, largoMax: number): number | null => {
  if (valor.length < largoMin || valor.length > largoMax || !esSoloDigitos(valor)) {
    return null;
  }
  return Number(valor);
};

/**
 * Separa fecha dd/MM/yyyy y hora HH:mm[:ss] sin expresiones regulares.
 * @param raw texto de fecha API
 * @returns partes de fecha/hora o null
 */
const separarFechaHoraLocal = (
  raw: string
): { dia: number; mes: number; anio: number; hora: number; minuto: number; segundo: number } | null => {
  const normalizado = raw.includes('–') ? raw.split('–').join('-') : raw;
  let fechaParte = normalizado;
  let horaParte = CADENA_VACIA;

  const indiceGuion = normalizado.indexOf(' - ');
  if (indiceGuion >= 0) {
    fechaParte = normalizado.slice(0, indiceGuion).trim();
    horaParte = normalizado.slice(indiceGuion + 3).trim();
  } else {
    const indiceEspacio = normalizado.lastIndexOf(' ');
    if (indiceEspacio > 0) {
      const posibleHora = normalizado.slice(indiceEspacio + 1).trim();
      if (posibleHora.indexOf(':') >= 0) {
        fechaParte = normalizado.slice(0, indiceEspacio).trim();
        horaParte = posibleHora;
      }
    }
  }

  if (horaParte === CADENA_VACIA) {
    return null;
  }

  const partesFecha = fechaParte.split('/');
  if (partesFecha.length !== 3) {
    return null;
  }

  const dia = parsearEnteroAcotado(partesFecha[0], 1, LARGO_DOS_DIGITOS);
  const mes = parsearEnteroAcotado(partesFecha[1], 1, LARGO_DOS_DIGITOS);
  const anio = parsearEnteroAcotado(partesFecha[2], 4, 4);
  if (dia === null || mes === null || anio === null) {
    return null;
  }

  const partesHora = horaParte.split(':');
  if (partesHora.length < 2 || partesHora.length > 3) {
    return null;
  }

  const hora = parsearEnteroAcotado(partesHora[0], 1, LARGO_DOS_DIGITOS);
  const minuto = parsearEnteroAcotado(partesHora[1], LARGO_DOS_DIGITOS, LARGO_DOS_DIGITOS);
  const segundo =
    partesHora.length === 3 ? parsearEnteroAcotado(partesHora[2], LARGO_DOS_DIGITOS, LARGO_DOS_DIGITOS) : SEGUNDOS_POR_DEFECTO;
  if (hora === null || minuto === null || segundo === null) {
    return null;
  }

  return { dia, mes, anio, hora, minuto, segundo };
};

/**
 * Parsea solo dd/MM/yyyy sin hora.
 * @param raw texto de fecha
 * @returns Date local o null
 */
const parsearSoloFechaLocal = (raw: string): Date | null => {
  const partesFecha = raw.split('/');
  if (partesFecha.length !== 3) {
    return null;
  }
  const dia = parsearEnteroAcotado(partesFecha[0], 1, LARGO_DOS_DIGITOS);
  const mes = parsearEnteroAcotado(partesFecha[1], 1, LARGO_DOS_DIGITOS);
  const anio = parsearEnteroAcotado(partesFecha[2], 4, 4);
  if (dia === null || mes === null || anio === null) {
    return null;
  }
  const soloFecha = parse(
    `${String(dia).padStart(LARGO_DOS_DIGITOS, CARACTER_RELLENO)}/${String(mes).padStart(LARGO_DOS_DIGITOS, CARACTER_RELLENO)}/${anio}`,
    FORMATO_DIA_MES_ANIO,
    new Date()
  );
  return isValid(soloFecha) ? soloFecha : null;
};

/**
 * Valida forma mínima ISO yyyy-MM-dd… sin regex y delega en parseISO.
 * @param raw texto ISO del API
 * @returns Date o null
 */
const parsearIsoEstricto = (raw: string): Date | null => {
  if (raw.length < 10) {
    return null;
  }
  if (raw.charAt(4) !== '-' || raw.charAt(7) !== '-') {
    return null;
  }
  if (!esSoloDigitos(raw.slice(0, 4)) || !esSoloDigitos(raw.slice(5, 7)) || !esSoloDigitos(raw.slice(8, 10))) {
    return null;
  }
  if (raw.length > 10) {
    const separador = raw.charAt(10);
    if (separador !== 'T' && separador !== ' ') {
      return null;
    }
  }
  const fromIso = parseISO(raw);
  return isValid(fromIso) ? fromIso : null;
};

/**
 * Parsea de forma segura un string de fecha (ISO o dd/MM/yyyy [-] HH:mm).
 * Solo acepta formatos conocidos; no usa new Date(string) libre ni RegExp.
 * @param value fecha enviada por el API o el KML
 * @returns Date válido o null si el valor no se puede interpretar
 */
export const parseFlexibleDate = (value?: string | null): Date | null => {
  if (value === null || value === undefined || String(value).trim() === CADENA_VACIA) {
    return null;
  }

  const raw = String(value).trim();

  const partesLocal = separarFechaHoraLocal(raw);
  if (partesLocal) {
    const fechaLocal = new Date(
      partesLocal.anio,
      partesLocal.mes - DESPLAZAMIENTO_MES,
      partesLocal.dia,
      partesLocal.hora,
      partesLocal.minuto,
      partesLocal.segundo
    );
    return isValid(fechaLocal) ? fechaLocal : null;
  }

  const soloFecha = parsearSoloFechaLocal(raw);
  if (soloFecha) {
    return soloFecha;
  }

  return parsearIsoEstricto(raw);
};

/**
 * Normaliza Date o string a un Date válido.
 * @param value fecha ya parseada o texto a interpretar
 * @returns Date válido o null
 */
const toValidDate = (value: Date | string | null | undefined): Date | null => {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  return parseFlexibleDate(value);
};

/**
 * Tipo de casuística de fecha para pintar el TER o el horario del corte.
 */
export type TerDisplayKind = typeof TIPO_FECHA_HOY | typeof TIPO_FECHA_MANANA | typeof TIPO_FECHA_OTRO_DIA | typeof TIPO_FECHA_VACIO;

/**
 * Texto de fecha listo para la UI, sin pasar por interpolación de i18n.
 */
export interface TerDisplay {
  kind: TerDisplayKind;
  /**
   * Texto principal listo para UI, sin interpolación.
   */
  principal: string;
  hora: string;
}

/**
 * Formatea el TER o un horario de corte según la casuística de producto.
 * Hoy a las HH:mm hrs., mañana a las HH:mm hrs. o dd/MM/yyyy a las HH:mm hrs.
 * @param ter fecha de reposición o de inicio del corte
 * @param labels textos traducidos de hoy, mañana y "a las"
 * @returns objeto con el texto principal y la hora, o vacío si no hay fecha
 */
export const formatTerDisplay = (
  ter: Date | string | null | undefined,
  labels?: { hoy?: string; manana?: string; aLas?: string }
): TerDisplay => {
  const fecha = toValidDate(ter);
  if (!fecha) {
    return { kind: TIPO_FECHA_VACIO, principal: CADENA_VACIA, hora: CADENA_VACIA };
  }

  const hora = format(fecha, FORMATO_HORA);
  const aLas = labels?.aLas ?? ETIQUETA_A_LAS;
  const hoyLabel = labels?.hoy ?? ETIQUETA_HOY;
  const mananaLabel = labels?.manana ?? ETIQUETA_MANANA;

  if (isToday(fecha)) {
    return { kind: TIPO_FECHA_HOY, principal: `${hoyLabel} ${aLas} ${hora}${SUFIJO_HORAS}`, hora };
  }
  if (isTomorrow(fecha)) {
    return { kind: TIPO_FECHA_MANANA, principal: `${mananaLabel} ${aLas} ${hora}${SUFIJO_HORAS}`, hora };
  }
  return {
    kind: TIPO_FECHA_OTRO_DIA,
    principal: `${format(fecha, FORMATO_DIA_MES_ANIO)} ${aLas} ${hora}${SUFIJO_HORAS}`,
    hora,
  };
};

/**
 * Formatea el TER como Hoy HH:mm, Mañana HH:mm o dd/MM/yyyy HH:mm.
 * @param ter fecha de reposición estimada
 * @param labels textos traducidos de hoy y mañana
 * @returns texto formateado o cadena vacía si no hay TER
 */
export const formatTerDate = (ter: Date | string | null | undefined, labels?: { hoy?: string; manana?: string }): string => {
  const display = formatTerDisplay(ter, labels);
  if (display.kind === TIPO_FECHA_VACIO) {
    return CADENA_VACIA;
  }
  if (display.kind === TIPO_FECHA_HOY) {
    return `${labels?.hoy ?? ETIQUETA_HOY} ${display.hora}`;
  }
  if (display.kind === TIPO_FECHA_MANANA) {
    return `${labels?.manana ?? ETIQUETA_MANANA} ${display.hora}`;
  }
  return `${format(toValidDate(ter) as Date, FORMATO_DIA_MES_ANIO)} ${display.hora}`;
};

/**
 * Obtiene solo la hora en formato HH:mm.
 * @param date fecha de la etapa del timeline
 * @returns hora formateada o cadena vacía si no hay fecha
 */
export const formatTimeOnly = (date: Date | string | null | undefined): string => {
  const fecha = toValidDate(date);
  if (!fecha) {
    return CADENA_VACIA;
  }
  return format(fecha, FORMATO_HORA);
};

/**
 * Formatea la última actualización para la UI en dd/MM/yyyy - HH:mm.
 * @param value fecha enviada por el API; si ya viene en ese formato, se normaliza
 * @returns fecha formateada o cadena vacía si no es válida
 */
export const formatLastUpdate = (value?: string | null): string => {
  if (value === null || value === undefined || String(value).trim() === CADENA_VACIA) {
    return CADENA_VACIA;
  }
  const raw = String(value).trim();
  /**
   * El tracking a veces ya envía la fecha como "06/08/2026 - 12:24".
   */
  const partesLocal = separarFechaHoraLocal(raw);
  if (partesLocal) {
    /**
     * Rellena día, mes, hora y minuto a dos dígitos.
     * @param valor fragmento numérico de la fecha
     * @returns fragmento con relleno
     */
    const rellenar = (valor: number) => String(valor).padStart(LARGO_DOS_DIGITOS, CARACTER_RELLENO);
    return `${rellenar(partesLocal.dia)}/${rellenar(partesLocal.mes)}/${partesLocal.anio}${SEPARADOR_FECHA_HORA}${rellenar(
      partesLocal.hora
    )}:${rellenar(partesLocal.minuto)}`;
  }
  const fecha = parseFlexibleDate(raw);
  if (!fecha) {
    return CADENA_VACIA;
  }
  return format(fecha, FORMATO_ULTIMA_ACTUALIZACION);
};

/**
 * Formatea la hora de notificación del corte futuro.
 * Si la creación es hoy, solo va la hora. Si es otro día, incluye la fecha.
 * @param notificacion fecha de creación del caso
 * @returns hora lista para la UI, o cadena vacía si no hay notificación
 */
export const formatHoraNotificacionCorte = (notificacion?: string | null): string => {
  const fechaNotificacion = parseFlexibleDate(notificacion);
  if (!fechaNotificacion) {
    return CADENA_VACIA;
  }
  if (isToday(fechaNotificacion)) {
    return `${format(fechaNotificacion, FORMATO_HORA)}${SUFIJO_HORAS}`;
  }
  return `${format(fechaNotificacion, FORMATO_ULTIMA_ACTUALIZACION)}${SUFIJO_HORAS}`;
};

/**
 * Texto del pie "información actualizada", listo para pintar sin interpolar la fecha en i18n.
 */
export type InfoActualizadaDisplay = {
  kind: typeof TIPO_FECHA_HOY | typeof TIPO_FECHA_OTRO_DIA | typeof TIPO_FECHA_VACIO;
  /**
   * Texto listo para UI, sin pasar por i18n la fecha con barras.
   */
  texto: string;
  hora: string;
  fechaFmt: string;
};

/**
 * Arma el pie del dashboard. Hoy usa "hoy a las HH:mm"; otro día usa "al dd/MM/yyyy - HH:mm".
 * @param value fecha de actualización del mapa
 * @param labels funciones de i18n que reciben solo la hora o la fecha ya formateada
 * @returns texto, hora y tipo de casuística, o vacío si no hay fecha
 */
export const formatInfoActualizada = (
  value?: string | null,
  labels?: { hoyALas?: (hora: string) => string; alFecha?: (fecha: string) => string }
): InfoActualizadaDisplay => {
  const fecha = parseFlexibleDate(value);
  const fechaFmt = formatLastUpdate(value);
  if (!fecha || fechaFmt === CADENA_VACIA) {
    return { kind: TIPO_FECHA_VACIO, texto: CADENA_VACIA, hora: CADENA_VACIA, fechaFmt: CADENA_VACIA };
  }
  const hora = format(fecha, FORMATO_HORA);
  if (isToday(fecha)) {
    return {
      kind: TIPO_FECHA_HOY,
      texto: labels?.hoyALas ? labels.hoyALas(hora) : `${PREFIJO_INFO_HOY}${hora}${SUFIJO_HORAS}`,
      hora,
      fechaFmt,
    };
  }
  return {
    kind: TIPO_FECHA_OTRO_DIA,
    texto: labels?.alFecha ? labels.alFecha(fechaFmt) : `${PREFIJO_INFO_OTRO_DIA}${fechaFmt}${SUFIJO_HORAS}`,
    hora,
    fechaFmt,
  };
};

/**
 * Devuelve solo el texto del pie de última actualización.
 * @param value fecha de actualización del mapa
 * @param labels funciones de i18n que reciben solo la hora o la fecha ya formateada
 * @returns texto del pie o cadena vacía
 */
export const formatFooterUpdate = (
  value?: string | null,
  labels?: { hoyALas?: (hora: string) => string; alFecha?: (fecha: string) => string }
): string => formatInfoActualizada(value, labels).texto;
